/** https://help.aliyun.com/document_detail/383947.html */
import OSS from 'ali-oss';
import { request } from '..';

const STATIC_PATH = '/aisou';
const CDN_ORIGIN = 'https://cloud.zhaomi.cn';
let client = null;

let _inProgressPromise = null;

async function getAssumeRole() {
  const CACHE_KEY = 'Upload_AssumeRoleData';
  const EXPIRE_TIME = 1000 * 60 * 30; // 30分钟

  const cachedData = sessionStorage.getItem(CACHE_KEY);
  if (cachedData) {
    const { data, timestamp } = JSON.parse(cachedData);
    if (Date.now() - timestamp < EXPIRE_TIME) {
      return data;
    }
  }

  // 如果已有请求在进行中，直接返回同一个 Promise
  if (_inProgressPromise) {
    return _inProgressPromise;
  }

  // 否则新建请求，并保存 Promise
  _inProgressPromise = request.post('/v1/aliy/assumerole').then(res => {
    _inProgressPromise = null; // 请求完成后清空 Promise
    const { code, data } = res;
    if (code === 0) {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ data, timestamp: Date.now() })
      );
      return data;
    }
    return {};
  }).catch(err => {
    _inProgressPromise = null;
    return {};
  });

  return _inProgressPromise;
}

async function getFileUniqueId(file) {
  return new Promise(async resolve => {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const id = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    resolve(id);
  });
}

async function createClient() {
  if (client) {
    return { client };
  }

  try {
    const { access_key_id, access_key_secret, security_token } =
      await getAssumeRole();

    if (!access_key_id) {
      return {
        client: null,
        error: new Error('Failed to get the access token.'),
      };
    }

    client = new OSS({
      // yourRegion填写Bucket所在地域。以华东1（杭州）为例，yourRegion填写为oss-cn-hangzhou。
      region: 'oss-cn-beijing',
      // 从STS服务获取的临时访问密钥（AccessKey ID和AccessKey Secret）。
      accessKeyId: access_key_id,
      accessKeySecret: access_key_secret,
      // 从STS服务获取的安全令牌（SecurityToken）。
      stsToken: security_token,
      // 填写Bucket名称。
      bucket: 'zm-cloud',
      // (secure: true) 则使用 HTTPS， (secure: false) 则使用 HTTP
      secure: true,
      useFetch: true,
      refreshSTSToken: async () => {
        const { access_key_id, access_key_secret, security_token } =
          await getAssumeRole();
        return {
          accessKeyId: access_key_id,
          accessKeySecret: access_key_secret,
          stsToken: security_token,
        };
      },
      refreshSTSTokenInterval: 30 * 60000,
    });

    return { client, error: null };
  } catch (error) {
    console.error('Failed to create OSS ==========', error);
    return { client: null, error };
  }
}

export async function isExistObject(name, options = {}) {
  try {
    const { client, error } = await createClient();
    if (error) return { ok: false, error };

    await client.head(name, options);
    return { ok: true };
  } catch (error) {}
  return { ok: false };
}

export async function ossMultipartUpload(name, data, options) {
  try {
    const { client, error } = await createClient();
    if (error) return { error };

    const { onProgress, ...reset } = options;
    options = {
      // 设置并发上传的分片数量。
      parallel: 4,
      // 设置分片大小。默认值为1 MB，最小值为100 KB。
      partSize: 1024 * 1024,
      ...reset,
    };

    let abortCheckpoint;

    const upload = () => {
      return client.multipartUpload(name, data, {
        progress: (progress, cpt, res) => {
          abortCheckpoint = cpt;
          onProgress?.({ progress, abort });
        },
        ...options,
      });
    };

    const abort = () => {
      if (abortCheckpoint) {
        client.abortMultipartUpload(
          abortCheckpoint.name,
          abortCheckpoint.uploadId
        );
      }
    };

    return { upload, abort };
  } catch (error) {
    console.error('Failed to multipartUpload ==========', error);
    return { error };
  }
}

export async function upload(file, options) {
  const { progress, success, fail, begin } = options;

  const { name, type, size, path = '' } = file;
  const objectURL = URL.createObjectURL(file);

  const responseMessage = (data, eventType) => {
    switch (eventType) {
      case 'begin':
        begin && begin(data);
        break;
      case 'fail':
        fail && fail(data);
        URL.revokeObjectURL(objectURL);
        break;
      case 'progress':
        progress && progress(data);
        break;
      case 'success':
        success && success(data);
        URL.revokeObjectURL(objectURL);
        break;
    }
  };

  try {
    const blob = new Blob([file], { type: file.type });
    const uniqueId = await getFileUniqueId(blob);
    const uploadFilePath = [
      STATIC_PATH,
      '/',
      uniqueId.slice(0,20),
      name.slice(name.lastIndexOf('.')),
    ].join('');

    responseMessage({}, 'begin');

    const responseSuccessMessage = () => {
      responseMessage(
        { url: CDN_ORIGIN + uploadFilePath, name, type, size, path },
        'success'
      );
    };

    {
      const { ok, error } = await isExistObject(uploadFilePath);
      if (ok) {
        responseSuccessMessage();
        return;
      } else if (error) {
        responseMessage({ message: error.toString() }, 'fail');
        return;
      }
    }

    const { upload, error } = await ossMultipartUpload(
      uploadFilePath,
      blob,
      {
        onProgress: ({ progress }) => {
          responseMessage({ progress }, 'progress');
        },
      }
    );
    if (error) {
      responseMessage({ message: error.toString() }, 'fail');
      return;
    }

    const { res } = await upload();
    if (res.status === 200) {
      responseSuccessMessage();
    }
  } catch (error) {
    responseMessage({ message: error.toString() }, 'fail');
  }
}
